import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';

interface SegmentedOption {
  value: string;
  label: string;
}

interface SegmentedProps extends FormlyFieldProps {
  options?: SegmentedOption[] | any;
}

/**
 * A two-or-more option segmented control, on Kendo's ButtonGroup. (CHAT-995)
 *
 * The distinction from `modern-switch-wrapper` is semantic, not decorative: a switch
 * says on/off — a state and its absence — and renders only the label of where you are.
 * Peers like Light and Dark are neither, and both names have to be on screen for the
 * choice to be legible before you make it.
 *
 * It binds the option's own value straight onto the form control, so a string enum stays
 * a string enum end to end and needs no boolean round-trip.
 *
 * Kendo rather than `FieldType.ButtonRadio`: that one is Bootstrap-flavoured
 * (`btn-outline-primary`), is used by nothing in the app, and the Portal standardised on
 * Kendo as its component vocabulary. `ButtonsModule` is already imported and exported by
 * SharedModule; `kendo-buttongroup` is in the pinned @progress/kendo-angular-buttons
 * 17.2.0.
 */
@Component({
  standalone: false,
  selector: 'formly-field-segmented',
  templateUrl: './segmented.component.html',
  styleUrls: ['./segmented.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldSegmented extends FieldType<FieldTypeConfig<SegmentedProps>> {
  /**
   * Named `segments`, not `options`: FieldType already declares an `options` member, and
   * shadowing it with a narrower type is a compile error.
   */
  get segments(): SegmentedOption[] {
    return (this.props.options as SegmentedOption[]) ?? [];
  }

  isSelected(option: SegmentedOption): boolean {
    return this.formControl.value === option.value;
  }

  select(option: SegmentedOption): void {
    if (this.formControl.disabled || this.isSelected(option)) return;
    this.formControl.setValue(option.value);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }
}
